import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentCard } from "@msuite/ui/content-card";
import { Divider } from "@msuite/ui/divider";
import type { Disclosure } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { TicketCard } from "@/components/cards";
import { useTicketRules } from "@/hooks/use-ticket-rules";
import { ProjectTicketsStateContainer } from "@/sites/projects/project/panels/project-timeline/project-tickets-state-container";
import type { ProjectTicketsController } from "./use-project-tickets-controller";

/** Props Interface */
interface ProjectTicketsFormProps {
	projectTicketsController: ProjectTicketsController;
	disclosure: Disclosure;
	/** The site the queued tickets land on, or null while the new-project form is still creating it. */
	projectId: string | null;
}

export function ProjectTicketsForm({
	projectTicketsController,
	disclosure,
	projectId,
}: ProjectTicketsFormProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { createIsDenied } = useTicketRules();

	/** Computed */
	const cannotCreate = createIsDenied(projectId);

	/** Render */
	return (
		<div>
			<ContentCard
				title={t("projects.new_tickets")}
				icon={icon("Sparkles")}
				variant="plain"
				className="v-stack gap-2"
				contentClassName="!p-0 -mx-2 -mb-2"
			>
				{!projectTicketsController.newTickets.length && (
					// The blank slate is the only way into the add-ticket modal from here, so denying
					// the rule leaves the section reading what is already on the site.
					<BlankSlate onClick={cannotCreate ? undefined : disclosure.onOpen} />
				)}
				{projectTicketsController.newTickets.map((ticket, id) => (
					<div
						key={`new-${id}`}
						className="card pb-1"
					>
						<TicketCard
							ticket={ticket as any}
							isDraggable={false}
							variant="plain"
						/>
					</div>
				))}
			</ContentCard>
			<Divider className="my-4" />
			<div className="v-stack gap-2">
				<ProjectTicketsStateContainer
					tickets={projectTicketsController.currentTickets}
					state="active"
					defaultIsOpen={true}
				/>
				<ProjectTicketsStateContainer
					tickets={projectTicketsController.currentTickets}
					state="inactive"
					defaultIsOpen={false}
				/>
				<ProjectTicketsStateContainer
					tickets={projectTicketsController.currentTickets}
					state="archived"
					defaultIsOpen={false}
				/>
			</div>
		</div>
	);
}
